/** 行事曆渲染(API 資料驅動)。自 public/js/calendar.js 移植。
 *
 *  三種視圖:日 / 週 / 月。依 RP 規格:
 *   - 迷你月曆:當天有 課程/作業/考試 任一 → 圓點;過期灰、未過期(含今日)藍
 *   - 週視圖:00:00-24:00 可捲動,錨點至當週最早課程
 *   - 月視圖:一天最多顯示 2 筆(由早至晚)
 *   - 類型篩選:全部 / 課程 / 作業 / 考試
 *   - AI 選課預排(preselect)以黃框顯示,僅檢視用
 */
import { request } from './api';
import type { CalendarEvent, CalendarEventType, CalendarFilters } from './types';

const TYPE_CLS: Record<CalendarEventType, string> = {
  course: 'cal-event--blue',
  assignment: 'cal-event--orange',
  exam: 'cal-event--teal',
  preselect: 'cal-event--yellow',
};
const TYPE_LABEL: Record<CalendarEventType, string> = {
  course: '課程', assignment: '作業', exam: '考試', preselect: '預排',
};
const WD = ['週日', '週一', '週二', '週三', '週四', '週五', '週六'];

// ── 日期工具 ──────────────────────────────────────────
function stripTime(d: Date): Date {
  const t = new Date(d);
  t.setHours(0, 0, 0, 0);
  return t;
}
const pad = (n: number) => String(n).padStart(2, '0');
const fmt = (d: Date) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
function addDays(d: Date, n: number): Date {
  const t = new Date(d);
  t.setDate(t.getDate() + n);
  return t;
}
/** 週日為一週起點 */
function weekStart(d: Date): Date {
  const t = stripTime(d);
  t.setDate(t.getDate() - t.getDay());
  return t;
}
const monthGridStart = (d: Date) => weekStart(new Date(d.getFullYear(), d.getMonth(), 1));
const isSameDay = (a: Date, b: Date) => fmt(a) === fmt(b);

const state = {
  view: 'day' as 'day' | 'week' | 'month',
  selected: stripTime(new Date()),      // 目前選取日期
  events: [] as CalendarEvent[],        // 目前載入範圍內的事件
  loadedRange: null as string | null,
  filters: { course: true, assignment: true, exam: true, preselect: true } as CalendarFilters,
};

const els: {
  miniGrid?: HTMLElement | null;
  schedule?: HTMLElement | null;
  rangeLabel?: HTMLElement | null;
  monthLabel?: HTMLElement | null;
} = {};

// ── 資料載入(涵蓋迷你月曆 6 週格 + 當前視圖)─────────
async function loadEvents(): Promise<void> {
  const gridStart = monthGridStart(state.selected);
  const from = addDays(gridStart, -7);
  const to = addDays(gridStart, 49);
  const key = `${fmt(from)}_${fmt(to)}`;
  if (state.loadedRange === key) return;
  state.events = await request<CalendarEvent[]>(
    `/api/student/calendar?from=${fmt(from)}&to=${fmt(to)}`);
  state.loadedRange = key;
}

const filtered = () => state.events.filter((e) => state.filters[e.type]);
const eventsOn = (dateStr: string) => filtered().filter((e) => e.date === dateStr);

const toMin = (hhmm: string) => Number(hhmm.slice(0, 2)) * 60 + Number(hhmm.slice(3));

// ── 迷你月曆 ──────────────────────────────────────────
function renderMiniCal(): void {
  if (!els.miniGrid) return;
  const today = stripTime(new Date());
  const gridStart = monthGridStart(state.selected);
  const month = state.selected.getMonth();
  const selWeekStart = fmt(weekStart(state.selected));
  let html = '';

  for (let w = 0; w < 6; w += 1) {
    const rowStart = addDays(gridStart, w * 7);
    const isSelWeek = state.view === 'week' && fmt(rowStart) === selWeekStart;
    const rowStyle = 'display:grid;grid-template-columns:repeat(7,1fr);'
      + (isSelWeek ? 'background:#EFEDFB;border-radius:12px;' : '');
    html += `<div style="${rowStyle}">`;

    for (let i = 0; i < 7; i += 1) {
      const day = addDays(rowStart, i);
      const dStr = fmt(day);
      const dim = day.getMonth() !== month ? 'color:#C9C9C9;' : 'color:#333;';
      const hasEvent = eventsOn(dStr).length > 0;
      const dotColor = day < today ? '#C9C9C9' : '#6C61E1'; // 過期灰、未過期(含今日)藍

      let inner: string;
      if (isSameDay(day, today)) {
        inner = `<span class="cal-mini-today">${day.getDate()}</span>`;
      } else if (isSameDay(day, state.selected)) {
        inner = '<span style="width:36px;height:36px;display:flex;align-items:center;'
          + 'justify-content:center;border:2px solid #6C61E1;border-radius:50%;'
          + `color:#6C61E1;font-weight:600">${day.getDate()}</span>`;
      } else {
        inner = `<span style="${dim}">${day.getDate()}</span>`;
      }

      html += `<div data-date="${dStr}" style="height:62px;display:flex;flex-direction:column;`
        + 'align-items:center;justify-content:center;font-size:18px;position:relative;cursor:pointer">'
        + inner
        + (hasEvent ? `<span class="cal-mini-cell__dot" style="background:${dotColor}"></span>` : '')
        + '</div>';
    }
    html += '</div>';
  }

  els.miniGrid.innerHTML = html;
  els.miniGrid.querySelectorAll<HTMLElement>('[data-date]').forEach((cell) => {
    cell.addEventListener('click', () => {
      state.selected = stripTime(new Date(`${cell.dataset.date}T00:00:00`));
      void refresh();
    });
  });
}

const metaText = (e: CalendarEvent) =>
  [e.department, `課號 ${e.code}`, e.courseType].filter(Boolean).join(' · ');

// ── 日視圖:時間軸 + 絕對定位事件卡 ──────────────────
function renderDay(): void {
  if (!els.schedule) return;
  const dayEvents = eventsOn(fmt(state.selected));
  let startHour = 8;
  let endHour = 18;

  for (const e of dayEvents) {
    const sh = parseInt(e.start, 10);
    const eh = Math.ceil(Number(e.end.slice(0, 2)) + (Number(e.end.slice(3)) > 0 ? 1 : 0));
    if (sh < startHour) startHour = Math.max(0, sh - 1);
    if (eh > endHour) endHour = Math.min(24, eh + 1);
  }

  const pxPerHour = 48;
  const height = (endHour - startHour) * pxPerHour + 48;
  let html = `<div style="position:relative;height:${height}px;padding:0 0 0 28px">`;

  for (let hToken = startHour; hToken <= endHour; hToken += 2) {
    const top = 24 + (hToken - startHour) * pxPerHour;
    let label: string;
    if (hToken === 12) label = '12PM';
    else if (hToken < 12) label = `${hToken}AM`;
    else label = `${hToken - 12}PM`;
    html += `<span style="position:absolute;left:0;top:${top}px;font-size:15px;color:#6C61E1">${label}</span>`;
    html += `<div style="position:absolute;left:56px;right:0px;top:${top + 6}px;border-top:1px solid #EDEDED"></div>`;
  }

  if (!dayEvents.length) {
    html += '<div style="position:absolute;left:64px;top:40px;color:#8C8C8C;font-size:15px">'
      + '本日沒有課程、作業或考試</div>';
  }

  for (const e of dayEvents) {
    const sMin = toMin(e.start);
    let eMin = toMin(e.end);
    if (eMin - sMin < 45) eMin = sMin + 45; // 作業/短事件最小高度
    const top = 30 + ((sMin - startHour * 60) / 60) * pxPerHour;
    const h = ((eMin - sMin) / 60) * pxPerHour;
    html += `<div class="cal-event ${TYPE_CLS[e.type] || 'cal-event--blue'}" `
      + `style="position:absolute;left:64px;right:8px;top:${top}px;height:${h}px">`
      + `<div class="cal-event__time">${e.start} - ${e.end}`
      + (e.type !== 'course' ? ` · ${TYPE_LABEL[e.type]}` : '')
      + '</div>'
      + '<div style="display:flex;justify-content:space-between;align-items:flex-end;">'
      + `<p class="cal-event__title">${e.title}</p>`
      + `<div class="cal-event__meta">${metaText(e)}`
      + (e.credits ? `<span class="badge badge--green cal-event__tag">${e.credits}學分</span>` : '')
      + '</div></div></div>';
  }

  els.schedule.innerHTML = `${html}</div>`;
}

// ── 週視圖:00:00-24:00 可捲動,錨點至最早課程 ────────
function renderWeek(): void {
  if (!els.schedule) return;
  const ws = weekStart(state.selected);
  const pxPerHour = 42;
  const days: Date[] = [];
  for (let i = 0; i < 7; i += 1) days.push(addDays(ws, i));

  let html = '<div style="display:grid;grid-template-columns:48px repeat(7,1fr);'
    + 'border-bottom:1px solid #EDEDED;padding:0 24px"><div></div>';
  for (const day of days) {
    const hi = isSameDay(day, state.selected);
    html += `<div style="padding:10px 6px;text-align:center;${hi ? 'background:#EFEDFB;border-top:4px solid #6C61E1;' : ''}">`
      + `<span style="font-size:20px;font-weight:500;color:#333">${day.getDate()}</span> `
      + `<span style="font-size:16px;color:#494949">${WD[day.getDay()]}</span></div>`;
  }
  html += '</div>';

  html += '<div id="cal-week-scroll" style="position:relative;padding:0 24px;height:440px;overflow-y:auto">'
    + '<div style="position:relative;display:grid;grid-template-columns:48px repeat(7,1fr)">';
  for (let hh = 0; hh < 24; hh += 1) {
    let label: string;
    if (hh === 0) label = '12AM';
    else if (hh === 12) label = '12PM';
    else if (hh < 12) label = `${hh}AM`;
    else label = `${hh - 12}PM`;
    html += `<div style="height:${pxPerHour}px;font-size:13px;color:#6C61E1;padding-top:2px">${label}</div>`;
    for (let d = 0; d < 7; d += 1) {
      html += `<div style="height:${pxPerHour}px;border-left:1px solid #F0F0F0;border-bottom:1px solid #F0F0F0"></div>`;
    }
  }
  html += '</div>';

  const colW = 'calc((100% - 48px - 48px) / 7)';
  let earliestCourseTop: number | null = null;
  days.forEach((day, di) => {
    for (const e of eventsOn(fmt(day))) {
      const sMin = toMin(e.start);
      let eMin = toMin(e.end);
      if (eMin - sMin < 40) eMin = sMin + 40;
      const top = (sMin / 60) * pxPerHour;
      const h = ((eMin - sMin) / 60) * pxPerHour;
      if (e.type === 'course' && (earliestCourseTop === null || top < earliestCourseTop)) {
        earliestCourseTop = top;
      }
      const left = `calc(48px + 24px + ${di} * ${colW} + 2px)`;
      html += `<div class="cal-event ${TYPE_CLS[e.type] || ''}" style="position:absolute;left:${left};`
        + `width:calc(${colW} - 6px);top:${top}px;height:${h}px;overflow:hidden">`
        + `<div class="cal-event__time">${e.start} - ${e.end}</div>`
        + `<div class="cal-event__title" style="font-size:13px">${e.title}</div></div>`;
    }
  });
  html += '</div>';
  els.schedule.innerHTML = html;

  // 錨點:當週最早課程(無課程則 8AM)
  const scroller = document.getElementById('cal-week-scroll');
  if (scroller) {
    const anchor = earliestCourseTop === null ? 8 * pxPerHour : earliestCourseTop;
    scroller.scrollTop = Math.max(0, anchor - 20);
  }
}

// ── 月視圖:每日最多 2 筆 ────────────────────────────
function renderMonth(): void {
  if (!els.schedule) return;
  const gridStart = monthGridStart(state.selected);
  const month = state.selected.getMonth();
  let html = '<div style="padding:0 24px">'
    + '<div style="display:grid;grid-template-columns:repeat(7,1fr);border:1px solid #EDEDED;'
    + 'border-radius:8px;overflow:hidden">';

  for (const d of WD) {
    html += '<div style="background:#F4F4F7;padding:10px;text-align:center;font-size:16px;'
      + `color:#555;border-bottom:1px solid #EDEDED">${d}</div>`;
  }

  for (let i = 0; i < 42; i += 1) {
    const day = addDays(gridStart, i);
    const dStr = fmt(day);
    const dim = day.getMonth() !== month ? 'color:#C9C9C9;' : 'color:#333;';
    const hi = isSameDay(day, state.selected)
      ? 'background:#F3F1FC;border-top:4px solid #6C61E1;' : '';
    const evs = eventsOn(dStr);
    let lines = evs.slice(0, 2).map((e) => {
      const name = e.title.length > 7 ? `${e.title.slice(0, 7)}…` : e.title;
      return '<div style="font-size:12px;color:#000;line-height:1.5;white-space:nowrap;'
        + `overflow:hidden;text-overflow:ellipsis">${e.start} ${name}</div>`;
    }).join('');
    if (evs.length > 2) {
      lines += `<div style="font-size:11px;color:#8C8C8C">+${evs.length - 2} 更多</div>`;
    }
    html += `<div data-date="${dStr}" style="min-height:96px;padding:8px 10px;`
      + `border-right:1px solid #F0F0F0;border-bottom:1px solid #F0F0F0;cursor:pointer;${hi}">`
      + `<div style="font-size:18px;font-weight:500;${dim}margin-bottom:4px">${day.getDate()}</div>`
      + `${lines}</div>`;
  }

  els.schedule.innerHTML = `${html}</div></div>`;
  els.schedule.querySelectorAll<HTMLElement>('[data-date]').forEach((cell) => {
    cell.addEventListener('click', () => {
      state.selected = stripTime(new Date(`${cell.dataset.date}T00:00:00`));
      state.view = 'day';
      syncSegButtons();
      void refresh();
    });
  });
}

// ── 工具列標籤 ────────────────────────────────────────
function renderLabels(): void {
  const d = state.selected;
  if (els.monthLabel) {
    els.monthLabel.textContent = `${d.getFullYear()}年${d.getMonth() + 1}月`;
  }
  if (!els.rangeLabel) return;
  if (state.view === 'day') {
    els.rangeLabel.textContent =
      `${d.getFullYear()}年,${d.getMonth() + 1}月${d.getDate()}日,${WD[d.getDay()]}`;
  } else if (state.view === 'week') {
    const ws = weekStart(d);
    const we = addDays(ws, 6);
    els.rangeLabel.textContent = `${d.getFullYear()}年,${ws.getMonth() + 1}月${ws.getDate()}日`
      + ` - ${we.getMonth() + 1}月${we.getDate()}日`;
  } else {
    els.rangeLabel.textContent = `${d.getFullYear()}年,${d.getMonth() + 1}月`;
  }
}

function syncSegButtons(): void {
  document.querySelectorAll<HTMLElement>('#cal-seg .cal-seg__btn').forEach((b) => {
    b.classList.toggle('is-active', b.dataset.view === state.view);
  });
}

async function refresh(): Promise<void> {
  await loadEvents();
  renderLabels();
  renderMiniCal();
  if (state.view === 'day') renderDay();
  if (state.view === 'week') renderWeek();
  if (state.view === 'month') renderMonth();
}

// ── 對外 API ──────────────────────────────────────────
export function setView(view: string): void {
  if (view !== 'day' && view !== 'week' && view !== 'month') return;
  state.view = view;
  syncSegButtons();
  void refresh();
}

export function shift(dir: number): void {
  if (state.view === 'month') {
    state.selected = new Date(state.selected.getFullYear(), state.selected.getMonth() + dir, 1);
  } else {
    const n = state.view === 'day' ? 1 : 7;
    state.selected = addDays(state.selected, dir * n);
  }
  void refresh();
}

export function goToday(): void {
  state.selected = stripTime(new Date());
  void refresh();
}

export function toggleFilter(type: string): CalendarFilters {
  if (type === 'all') {
    const on = !(state.filters.course && state.filters.assignment && state.filters.exam);
    state.filters = { course: on, assignment: on, exam: on, preselect: on };
  } else if (type === 'course' || type === 'assignment' || type === 'exam' || type === 'preselect') {
    state.filters[type] = !state.filters[type];
  }
  void refresh();
  return state.filters;
}

export const getFilters = (): CalendarFilters => state.filters;

export function init(): void {
  els.miniGrid = document.getElementById('cal-mini-grid');
  els.schedule = document.getElementById('cal-schedule');
  els.rangeLabel = document.getElementById('cal-range-label');
  els.monthLabel = document.getElementById('cal-month-label');
  void refresh();
}
